import React from 'react';
import Button from '../Button';
import Card from '../Card';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(_error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Check if this is a chunk loading error (happens after deployments)
    const isChunkLoadError =
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('Importing a module script failed') ||
      error?.message?.includes('is not a valid JavaScript MIME type') ||
      error?.message?.includes('Loading chunk') ||
      error?.name === 'ChunkLoadError';

    if (isChunkLoadError) {
      console.warn('🔄 Chunk load error detected in ErrorBoundary - forcing reload');
      // Clear service worker caches and reload
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => registration.unregister());
        });
      }
      // Force reload to get latest version
      window.location.reload();
      return;
    }

    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-base-200 p-4">
          <Card className="max-w-md w-full">
            <div className="card-body text-center">
              <div className="text-6xl mb-4">😕</div>
              <h2 className="card-title text-error justify-center mb-2">
                Oops! Something went wrong
              </h2>
              <p className="text-base-content/70 mb-4">
                We encountered an unexpected error. Please try refreshing the page or contact support if the problem persists.
              </p>
              
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="collapse collapse-arrow border border-base-300 bg-base-100 rounded-box mb-4">
                  <input type="checkbox" /> 
                  <div className="collapse-title text-sm font-medium">
                    View Error Details (Development)
                  </div>
                  <div className="collapse-content"> 
                    <div className="text-left text-xs bg-base-300 p-2 rounded font-mono">
                      <div className="text-error font-bold mb-2">Error:</div>
                      <div className="mb-2">{this.state.error.toString()}</div>
                      {this.state.errorInfo.componentStack && (
                        <>
                          <div className="text-error font-bold mb-2">Component Stack:</div>
                          <pre className="whitespace-pre-wrap text-xs">
                            {this.state.errorInfo.componentStack}
                          </pre>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="card-actions justify-center gap-2">
                <Button 
                  onClick={this.handleRetry}
                  variant="primary"
                  size="sm"
                >
                  Try Again
                </Button>
                <Button 
                  onClick={() => window.location.reload()}
                  variant="outline"
                  size="sm"
                >
                  Refresh Page
                </Button>
              </div>
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;